import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import styles from './ProviderListCard.module.css';

const MAX_VISIBLE_PLANS = 4;

// Quantas pessoas têm carteirinha ativa desta operadora
const patientsLabel = (count) => {
    const total = Number(count) || 0;
    if (total === 0) return 'Ninguém com carteirinha';
    return `${total} ${total === 1 ? 'pessoa' : 'pessoas'} com carteirinha`;
};

// Procedimentos ativos na tabela da operadora
const proceduresLabel = (procedures) => {
    const total = (procedures || []).filter((procedure) => Number(procedure.active) === 1).length;
    if (total === 0) return 'Sem tabela de procedimentos';
    return `${total} ${total === 1 ? 'procedimento' : 'procedimentos'} na tabela`;
};

/** Ação compacta da linha: ícone com tooltip (o span mantém o tooltip no botão desligado). */
function RowAction({ title, label, disabled, onClick, Icon, tone }) {
    return (
        <Tooltip title={title} arrow>
            <span>
                <IconButton
                    size="small"
                    className={`${styles.rowAction} ${tone ? styles[tone] : ''}`}
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={label}
                >
                    <Icon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
}

/** Planos ativos da operadora como chips; além de quatro, resume em "+N". */
function PlanChips({ plans }) {
    const active = (plans || []).filter((plan) => Number(plan.active) === 1);
    if (active.length === 0) {
        return <span className={styles.noPlans}>Sem planos cadastrados</span>;
    }
    const visible = active.slice(0, MAX_VISIBLE_PLANS);
    const rest = active.slice(MAX_VISIBLE_PLANS);
    return (
        <>
            {visible.map((plan) => (
                <span key={plan.id || plan.name} className={styles.plan} title={plan.name}>{plan.name}</span>
            ))}
            {rest.length > 0 && (
                <Tooltip title={rest.map((plan) => plan.name).join(', ')} arrow>
                    <span className={`${styles.plan} ${styles.planMore}`}>+{rest.length}</span>
                </Tooltip>
            )}
        </>
    );
}

/**
 * Uma operadora: ícone, nome e registro ANS, planos, carteirinhas registradas,
 * situação e as ações (editar, ativar/inativar), só para quem pode editar.
 */
function ProviderRow({ provider, canEdit, busy, onEdit, onToggle, onProcedures }) {
    const inactive = Number(provider.active) !== 1;
    const secondary = provider.ans_registry
        ? `Registro ANS ${provider.ans_registry}`
        : (provider.notes || 'Sem registro ANS');

    return (
        <li className={`${styles.row} ${inactive ? styles.muted : ''}`}>
            <span className={`${styles.providerIcon} ${inactive ? styles.providerIconMuted : ''}`}>
                <ApartmentOutlinedIcon fontSize="small" />
            </span>

            <div className={styles.identity}>
                <span className={`${styles.name} ${styles.ellipsis}`}>{provider.name}</span>
                <span className={`${styles.secondary} ${styles.ellipsis}`} title={provider.notes || undefined}>
                    {secondary}
                </span>
            </div>

            <div className={styles.plans}>
                <PlanChips plans={provider.plans} />
            </div>

            <div className={styles.tags}>
                <span className={styles.detail}>
                    <PeopleOutlineIcon className={styles.detailIcon} />
                    {patientsLabel(provider.patients_count)}
                </span>
                <span className={styles.detail}>
                    <ListAltOutlinedIcon className={styles.detailIcon} />
                    {proceduresLabel(provider.procedures)}
                </span>
                {(Number(provider.requires_medical_request) === 1 || Number(provider.requires_authorization) === 1) && (
                    <span className={styles.requirements}>
                        {Number(provider.requires_medical_request) === 1 && <span className={`${styles.tag} ${styles.tagRequirement}`}>Exige pedido</span>}
                        {Number(provider.requires_authorization) === 1 && <span className={`${styles.tag} ${styles.tagRequirement}`}>Exige guia</span>}
                    </span>
                )}
                {inactive
                    ? <span className={`${styles.tag} ${styles.tagInactive}`}><span className={styles.dot} />Inativa</span>
                    : <span className={`${styles.tag} ${styles.tagActive}`}><span className={styles.dot} />Ativa</span>}
            </div>

            <span className={styles.actions}>
                <RowAction
                    title="Tabela de procedimentos"
                    label={`Tabela de procedimentos de ${provider.name}`}
                    disabled={busy}
                    onClick={() => onProcedures(provider)}
                    Icon={ListAltOutlinedIcon}
                />
            {canEdit && (
                <>
                    <RowAction
                        title="Editar operadora"
                        label={`Editar ${provider.name}`}
                        disabled={busy}
                        onClick={() => onEdit(provider)}
                        Icon={EditOutlinedIcon}
                    />
                    {inactive ? (
                        <RowAction
                            title="Ativar operadora"
                            label={`Ativar ${provider.name}`}
                            disabled={busy}
                            onClick={() => onToggle(provider)}
                            Icon={RestoreOutlinedIcon}
                            tone="positive"
                        />
                    ) : (
                        <RowAction
                            title="Inativar operadora"
                            label={`Inativar ${provider.name}`}
                            disabled={busy}
                            onClick={() => onToggle(provider)}
                            Icon={PowerSettingsNewOutlinedIcon}
                            tone="warning"
                        />
                    )}
                </>
            )}
            </span>
        </li>
    );
}

export default ProviderRow;
